import { type ReactNode } from 'react';
import { useInView } from 'react-intersection-observer';
import { useDidUpdate } from '@toss/react';
import { LoadingSpinner } from '@/shared/components';
import { cn } from '@/utils/tailwind.util';
import { type PexelsImage } from '@/views/build/hook/imageSearch/useImageSearch';
interface ContainerProps {
  children: ReactNode;
  hasNextPage: boolean;
  onLoadMore: () => void;
  className?: string;
}
const ImagesInfiniteListViewContainer = ({
  children,
  hasNextPage,
  onLoadMore,
  className
}: ContainerProps) => {
  const {
    ref,
    inView
  } = useInView({
    triggerOnce: false,
    threshold: 0.0,
    rootMargin: '0px 0px 1000px 0px'
  });
  useDidUpdate(() => {
    if (inView && hasNextPage) {
      onLoadMore();
    }
  }, [inView, hasNextPage]);
  return <div className={cn('flex flex-col justify-between pb-lg', className)} data-sentry-component="ImagesInfiniteListViewContainer" data-sentry-source-file="ImagesInfiniteListView.tsx">
      {children}
      <div ref={ref} style={{
      height: '1px'
    }} />
    </div>;
};
interface ContentProps {
  children: ReactNode;
  className?: string;
}
const ImagesInfiniteListViewContent = ({
  children,
  className
}: ContentProps) => {
  return <div className={cn('grid grid-cols-2 gap-[8px] tablet-sm:grid-cols-3 tablet-lg:grid-cols-4 laptop-lg:grid-cols-5', className)} data-sentry-component="ImagesInfiniteListViewContent" data-sentry-source-file="ImagesInfiniteListView.tsx">
      {children}
    </div>;
};
const ImagesInfiniteListViewItem = ({
  image,
  onImageSelect
}: {
  image: PexelsImage;
  onImageSelect: (imageUrl: string) => void;
}) => {
  const imageUrl = image.src.medium;
  return <div className="flex flex-col gap-2xs" data-sentry-component="ImagesInfiniteListViewItem" data-sentry-source-file="ImagesInfiniteListView.tsx">
      <div onClick={() => {
      onImageSelect(imageUrl);
    }} className="relative h-[200px] cursor-pointer rounded-2xs transition-opacity duration-150 hover:opacity-80">
        <div className="absolute inset-0 z-10 rounded-2xs bg-gray-100" />
        <img src={imageUrl} alt={image.alt} className="relative z-20 h-full w-full rounded-2xs object-cover" />
      </div>
      <a href={image.url} target="_blank">
        <span className="text-body-sm-medium text-assistive underline">
          {image.photographer}
        </span>
      </a>
    </div>;
};
const ImagesInfiniteListViewLoading = ({
  className
}: {
  className?: string;
}) => {
  return <div className={cn('flex h-[100px] items-center justify-center', className)} data-sentry-component="ImagesInfiniteListViewLoading" data-sentry-source-file="ImagesInfiniteListView.tsx">
      <LoadingSpinner className="size-8 text-primary" data-sentry-element="LoadingSpinner" data-sentry-source-file="ImagesInfiniteListView.tsx" />
    </div>;
};
export const ImagesInfiniteListView = {
  Container: ImagesInfiniteListViewContainer,
  Content: ImagesInfiniteListViewContent,
  Item: ImagesInfiniteListViewItem,
  Loading: ImagesInfiniteListViewLoading
};